import {useMemo, type FC} from 'react'
import type {Organization, Enterprise} from '@github-ui/repos-types'
import type {Repository} from '@github-ui/current-repository'
import {UserFilterProvider} from './providers/user'
import {RuleStatusFilterProvider} from './providers/rule-status'
import {ShaFilterProvider} from './providers/sha'
import {
  SecurityProductsFilterBar,
  parseAsUser,
  serializeLogin,
  filterDefinition,
  createdFilterDefinition,
  timePeriodFilterDefinition,
  evaluateStatusFilterDefinition,
  repositoryFilterDefinition,
  staticOrganizationFilterDefinition,
  rulesetFilterDefinition,
  type FilterDefinition,
} from './SecurityProductsFilterBar'
import type {InsightsFilter, Ruleset, SourceType} from '../../types/rules-types'
import {insightsIndexPath} from '../../helpers/insights-filter'
import {useInsightsActors} from '../../hooks/use-insights-actors'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'
import {useBranchFilterDefinitions} from '../../hooks/use-branch-filter-definitions'
import {isFeatureEnabled} from '@github-ui/feature-flags'

type InsightsFilterBar2Props = {
  branchIsValid?: boolean
  filter: InsightsFilter
  source: Repository | Organization | Enterprise
  sourceType: SourceType
  organizations?: string[]
  rulesets: Ruleset[]
}

export const InsightsFilterBar2: FC<InsightsFilterBar2Props> = ({
  branchIsValid,
  filter,
  source,
  sourceType,
  organizations,
  rulesets,
}) => {
  const repo = source as Repository
  const {definitions: branchDefinitions} = useBranchFilterDefinitions<InsightsFilter>({
    sourceType,
    ownerLogin: repo.ownerLogin,
    repoName: repo.name,
    branchIsValid,
    filterBranch: filter.branch,
  })
  const rulesInsightsFilterBarCreated = isFeatureEnabled('rules_insights_filter_bar_created')
  const showShaFilter = sourceType === 'repository'
  const {users: actors} = useInsightsActors()
  const {resolvePath} = useRelativeNavigation()
  const repoSuggestionsUrl = resolvePath('repo_suggestions')

  const filterDefinitions = useMemo<Array<FilterDefinition<InsightsFilter>>>(
    () => [
      ...(rulesInsightsFilterBarCreated
        ? [createdFilterDefinition<InsightsFilter>()]
        : [timePeriodFilterDefinition<InsightsFilter>()]),
      evaluateStatusFilterDefinition<InsightsFilter>(),
      ...branchDefinitions,
      ...(sourceType === 'organization' ? [repositoryFilterDefinition<InsightsFilter>(repoSuggestionsUrl)] : []),
      ...(sourceType === 'enterprise' && organizations?.length
        ? [staticOrganizationFilterDefinition<InsightsFilter>(organizations)]
        : []),
      rulesetFilterDefinition<InsightsFilter>(rulesets),
      filterDefinition<InsightsFilter>({
        config: {
          filterKey: 'actor',
          queryKey: 'user',
          parse: parseAsUser,
          serialize: (v: unknown) => serializeLogin(v as {login: string}),
        },
        provider: new UserFilterProvider(actors),
      }),
      filterDefinition<InsightsFilter>({
        config: {filterKey: 'ruleStatus', queryKey: 'rule-status'},
        provider: new RuleStatusFilterProvider(),
      }),
      ...(showShaFilter
        ? [
            filterDefinition<InsightsFilter>({
              config: {filterKey: 'afterOid', queryKey: 'sha'},
              provider: new ShaFilterProvider(),
            }),
          ]
        : []),
    ],
    [
      branchDefinitions,
      rulesInsightsFilterBarCreated,
      sourceType,
      repoSuggestionsUrl,
      organizations,
      rulesets,
      actors,
      showShaFilter,
    ],
  )

  return (
    <SecurityProductsFilterBar<InsightsFilter>
      id="rules-insights-filter"
      label="Filter rules insights"
      filter={filter}
      filterDefinitions={filterDefinitions}
      getIndexPath={insightsIndexPath}
    />
  )
}
